import { S } from "../Squaremap.js";
import L from "leaflet";

const HEAD_SIZE = 19;
/** Box the view cone is drawn in (screen px). The cone itself is a wedge of it. */
const BEAM_SIZE = 120;

/** Per-browser display settings, owned by Agonia.js (window global = no import cycle). */
function setting(key, def) {
    const s = window.agoniaSettings;
    return s && key in s ? s[key] : def;
}

/** Strip any HTML/colour formatting from a display name -> plain text. */
function plain(html) {
    const d = document.createElement("div");
    d.innerHTML = html == null ? "" : html;
    return d.textContent || d.innerText || "";
}

function attr(s) {
    return String(s == null ? "" : s)
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

// What a player is currently doing, drawn onto their head on the map. The plugin
// writes these keys into agonia-activity.json once a second.
//
// The artwork lives in frontend/public/images/activity/ as ordinary image files —
// edit or replace those to change how a state looks, no code change needed. They
// are pixel art traced from the in-game textures so they sit next to the (equally
// blocky) player heads: slate-blue elytra wings, the iron minecart, the oak boat
// with its oar, and the leather saddle.
//
// To swap one for a PNG, drop e.g. minecart.png in that folder and change the
// `icon` below to match — the extension is not assumed anywhere.
//
// The drawn size and where it sits on the head are CSS, not part of the image:
// see `.agonia-pact[data-act="..."]` in css/styles.css. Keep a replacement's
// aspect ratio close to the original or adjust that rule to suit.
const ACTIVITY_DIR = "images/activity/";
const ACTIVITY = {
    // wings drawn BEHIND the head; the other three are corner badges in front
    elytra: { label: "Flyger med elytra", icon: "elytra.svg" },
    minecart: { label: "Åker minecart", icon: "minecart.svg" },
    boat: { label: "Åker båt", icon: "boat.svg" },
    riding: { label: "Rider", icon: "riding.svg" },
};

/** The activity key for a uuid, from the map Agonia.js keeps up to date. */
function activityFor(uuid) {
    const m = window.agoniaActivity;
    if (!m) return null;
    const key = String(uuid || "").replace(/-/g, "");
    const kind = m[key];
    return kind && ACTIVITY[kind] ? kind : null;
}

class Player {
    /** @type {string} */ name;
    /** @type {string} */ uuid;
    /** @type {string} */ world;
    /** @type {string} */ displayName;
    /** @type {number} */ x;
    /** @type {number} */ z;
    /** @type {number} */ armor;
    /** @type {number} */ health;
    /** @type {number | null} */ yaw;
    /** @type {L.Tooltip} */ tooltip;
    /** @type {L.Marker} */ marker;
    /** @type {L.Marker} */ beam;

    constructor(json) {
        this.name = json.name;
        this.uuid = json.uuid;
        this.world = json.world;
        this.displayName = json.display_name !== undefined ? json.display_name : json.name;
        this.x = 0;
        this.z = 0;
        this.armor = json.armor != null ? json.armor : 0;
        this.health = json.health != null ? json.health : 20;
        this.yaw = typeof json.yaw === "number" && isFinite(json.yaw) ? json.yaw : null;
        this.lastJson = json;

        // The marker IS the player's head, rendered in the low-z players pane so
        // it always sits under category markers.
        this.marker = L.marker(S.toLatLng(json.x, json.z), { icon: this.makeIcon(), pane: "agonia-players" });

        // The "flashlight": a soft cone of light showing where the player looks.
        // Own pane BELOW the heads, non-interactive, so it never eats clicks.
        this.beamAngle = this.yaw == null ? 0 : this.yaw;
        this.beamAdded = false;
        this.beam = L.marker(S.toLatLng(json.x, json.z), {
            icon: L.divIcon({
                className: "agonia-beam",
                html: '<i class="agonia-beam-cone"></i>',
                iconSize: [BEAM_SIZE, BEAM_SIZE],
                iconAnchor: [BEAM_SIZE / 2, BEAM_SIZE / 2],
            }),
            pane: "agonia-beam",
            interactive: false,
            keyboard: false,
        });

        // The always-on nameplate is its OWN marker (not a tooltip), so it glides
        // in perfect lockstep with the head the same way the beam does: a marker's
        // position is rounded and CSS-transitioned identically, and we move it in
        // the same setLatLng call as the head. A tooltip goes through a different,
        // laggier path (Leaflet's _moveTooltip + its own follow machinery). The
        // icon is a zero-size point at the head; the pill floats above it via CSS.
        this.nameAdded = false;
        this.nameMarker = L.marker(S.toLatLng(json.x, json.z), {
            icon: this.makeNameIcon(),
            pane: "nameplate",
            interactive: false,
            keyboard: false,
        });

        // Name tag: on hover (a transient tooltip), or permanently (the marker above).
        this.bindName();

        this.marker.on("mouseover", () => this.hoverName(true));
        this.marker.on("mouseout", () => this.hoverName(false));

        // Click -> head + name + health + armor + world.
        this.marker.bindPopup(() => this.makeInfoContent(this.lastJson), { offset: [0, -HEAD_SIZE / 2] });

        // Right-click -> player context menu (copy coords; staff: teleport / invsee / history).
        this.marker.on("contextmenu", (e) => {
            if (e.originalEvent) e.originalEvent.preventDefault();
            L.DomEvent.stopPropagation(e);
            if (window.agoniaOnPlayerContext) {
                window.agoniaOnPlayerContext(
                    { name: this.name, x: this.x, z: this.z, world: this.world },
                    e.originalEvent,
                );
            }
        });
    }

    /**
     * Leaflet attaches DOM focus/blur listeners to the marker element whenever a
     * HOVER tooltip is bound, and unbindTooltip() only ever removes the *layer*
     * handlers — those DOM ones are left behind. The blur one calls closeTooltip,
     * so once a hover tooltip has been swapped for a permanent nameplate,
     * clicking a head (markers are focusable) and then clicking away would close
     * that nameplate for good, since permanent tooltips only reopen on `add`.
     * Drop it before rebinding.
     *
     * The matching focus listeners are anonymous, so they can't be removed by
     * reference — but all they do is OPEN the tooltip, which is harmless.
     */
    dropStaleBlurListener() {
        const el = this.marker.getElement();
        if (el) {
            L.DomEvent.off(el, "blur", this.marker.closeTooltip, this.marker);
        }
    }

    /** (Re)bind the name tag for the current "visa namn alltid" setting:
     *   - ALWAYS on  -> the nameplate MARKER (glides with the head); no tooltip.
     *   - hover only -> a transient tooltip that appears while the head is hovered.
     */
    bindName() {
        const always = !!setting("names", true);
        if (this.nameInited && this.nameAlways === always) {
            return;
        }
        this.nameInited = true;
        this.nameAlways = always;

        if (always) {
            // hover tooltip (if any) gives way to the permanent marker
            if (this.marker.getTooltip()) {
                this.marker.unbindTooltip();
            }
            this.dropStaleBlurListener();
            this.tooltip = null;
            this.syncName();
            this.paintName();
        } else {
            // no permanent nameplate -> a hover-only tooltip
            this.syncName();
            if (!this.marker.getTooltip()) {
                this.tooltip = L.tooltip({
                    direction: "top",
                    offset: [0, -HEAD_SIZE / 2 - 2],
                    pane: "nameplate",
                    permanent: false,
                    interactive: false,
                });
                this.tooltip.setContent(this.makeNameContent());
                this.marker.bindTooltip(this.tooltip);
            }
        }
    }

    /** A zero-size point at the head's position; the pill floats above it via CSS
     *  (.agonia-name-pos). Being a marker is the whole point — it rides the same
     *  rounded, CSS-transitioned positioning as the head, so it never lags. */
    makeNameIcon() {
        return L.divIcon({
            className: "agonia-nameplate-marker",
            html: '<span class="agonia-name-pos"><span class="agonia-name-in"></span></span>',
            iconSize: [0, 0],
            iconAnchor: [0, 0],
        });
    }

    /** Write the current name into the nameplate marker's pill (when it's on the
     *  map). Cheap; the last-shown name is remembered so update() only repaints on
     *  an actual change. */
    paintName() {
        const el = this.nameMarker && this.nameMarker.getElement();
        if (el) {
            const pill = el.querySelector(".agonia-name-in");
            if (pill) {
                pill.textContent = plain(this.displayName);
            }
        }
        this.shownName = this.displayName;
    }

    /** Add/remove the nameplate marker to match the setting + whether the head is
     *  on the map. Mirrors syncBeam. */
    syncName() {
        const want = this.nameAlways && S.playerList.markers.has(this.uuid);
        if (want === this.nameAdded) {
            return;
        }
        if (want) {
            this.nameMarker.setLatLng(this.marker.getLatLng());
            this.nameMarker.addTo(S.layerControl.playersLayer);
            this.nameAdded = true;
            this.paintName();
        } else {
            S.layerControl.playersLayer.removeLayer(this.nameMarker);
            S.map.removeLayer(this.nameMarker);
            this.nameAdded = false;
        }
    }

    /** The name goes in a span of its own so the hover grow has something to
     *  scale that isn't the tooltip box (Leaflet owns that box's transform). Used
     *  only for the hover tooltip now — the marker sets its pill text directly. */
    makeNameContent() {
        const inner = document.createElement("span");
        inner.className = "agonia-name-in";
        inner.textContent = plain(this.displayName);
        this.shownName = this.displayName;
        return inner;
    }

    /** Hovering the head grows its name tag too. Whichever form is active (the
     *  always-on marker or the hover tooltip) lives in its own pane, so CSS :hover
     *  can't reach it — the head's hover events carry the state across. */
    hoverName(on) {
        const el = this.nameAlways
            ? this.nameMarker && this.nameMarker.getElement()
            : this.tooltip && this.tooltip.getElement();
        if (el) {
            el.classList.toggle("agonia-name-hot", on);
        }
    }

    /** Turn a Minecraft yaw into the cone's CSS rotation, taking the SHORT way
     *  round so it never spins 350° when the player turns 10° past the wrap. */
    aimBeam(yaw) {
        if (typeof yaw !== "number" || !isFinite(yaw)) {
            return;
        }
        this.yaw = yaw;
        const norm = (a) => ((a % 360) + 360) % 360;
        let delta = norm(yaw) - norm(this.beamAngle);
        if (delta > 180) {
            delta -= 360;
        } else if (delta < -180) {
            delta += 360;
        }
        this.beamAngle += delta;
        this.paintBeam();
    }

    /** The cone points SOUTH (down) at 0°, which is exactly Minecraft's yaw 0, so
     *  the rotation is the raw yaw. (Map is north-up: lat = -z.) */
    paintBeam() {
        const el = this.beam.getElement();
        const cone = el && el.firstElementChild;
        if (cone) {
            cone.style.transform = "rotate(" + this.beamAngle.toFixed(1) + "deg)";
        }
    }

    /** Add/remove the cone to match the setting + whether the head is on the map. */
    syncBeam() {
        const want = !!setting("flashlight", true) && this.yaw != null && S.playerList.markers.has(this.uuid);
        if (want === this.beamAdded) {
            return;
        }
        if (want) {
            this.beam.setLatLng(this.marker.getLatLng());
            this.beam.addTo(S.layerControl.playersLayer);
            this.beamAdded = true;
            this.paintBeam();
        } else {
            S.layerControl.playersLayer.removeLayer(this.beam);
            S.map.removeLayer(this.beam);
            this.beamAdded = false;
        }
    }

    /** A div icon (not a plain image) so the head can carry an activity badge —
     *  elytra wings behind it, or a minecart/boat/saddle marker in the corner.
     *  Head and badge share an inner group so the hover grow scales them as one
     *  unit, around the head's centre — scaling them separately would slide the
     *  head out of the minecart it is sitting in. */
    makeIcon() {
        return L.divIcon({
            html:
                '<span class="agonia-phead-in">' +
                '<img class="agonia-playerhead" src="' +
                attr(this.getHeadUrl()) +
                '" alt="">' +
                "</span>",
            iconSize: [HEAD_SIZE, HEAD_SIZE],
            iconAnchor: [HEAD_SIZE / 2, HEAD_SIZE / 2],
            popupAnchor: [0, -HEAD_SIZE / 2],
            tooltipAnchor: [0, 0],
            className: "agonia-phead",
        });
    }

    /** Draw (or clear) the activity badge on the head. Cheap to call every tick:
     *  the last-drawn kind is remembered ON the element, so a head whose element
     *  Leaflet recreated repaints itself automatically. */
    applyActivity() {
        const el = this.marker.getElement();
        if (!el) {
            return;
        }
        const kind = activityFor(this.uuid);
        if (el._agActivity === kind) {
            return;
        }
        el._agActivity = kind;
        const old = el.querySelector(".agonia-pact");
        if (old) {
            old.remove();
        }
        el.classList.toggle("agonia-phead-elytra", kind === "elytra");
        if (!kind) {
            return;
        }
        const badgeEl = document.createElement("i");
        badgeEl.className = "agonia-pact";
        badgeEl.dataset.act = kind;
        badgeEl.title = ACTIVITY[kind].label;
        badgeEl.innerHTML = '<img src="' + attr(ACTIVITY_DIR + ACTIVITY[kind].icon) + '" alt="">';
        // into the head's group, so the two grow together on hover
        const group = el.querySelector(".agonia-phead-in") || el;
        // wings go behind the head, corner badges on top of it
        group.insertBefore(badgeEl, kind === "elytra" ? group.firstChild : null);
    }

    getHeadUrl() {
        const np =
            S.worldList.curWorld &&
            S.worldList.curWorld.player_tracker &&
            S.worldList.curWorld.player_tracker.nameplates;
        const tpl = (np && np.heads_url) || "https://mc-heads.net/avatar/{uuid}/32";
        return tpl.replace(/{uuid}/g, this.uuid).replace(/{name}/g, this.name);
    }

    makeInfoContent() {
        const m = window.agoniaPlayerStatus || {};
        const status = m[this.uuid] || m[String(this.uuid).replace(/-/g, "")] || {};
        const c = document.createElement("div");
        c.className = "agonia-pinfo";

        // head with an optional crown (gold = prime, aqua = diamond prime)
        const headWrap = document.createElement("div");
        headWrap.className = "pinfo-head-wrap";
        const head = document.createElement("img");
        head.src = this.getHeadUrl();
        head.className = "pinfo-head";
        headWrap.appendChild(head);
        // top crown (ägare > diamond prime > gold prime) + staff role shield
        if (window.agoniaCrown) headWrap.insertAdjacentHTML("beforeend", window.agoniaCrown(status));
        // ägare shows only the crown, never the staff shield
        if (status.role && !status.agare && window.agoniaRoleBadge) {
            headWrap.insertAdjacentHTML("beforeend", window.agoniaRoleBadge(status.role));
        }
        c.appendChild(headWrap);

        const col = document.createElement("div");
        col.className = "pinfo-col";

        const nm = document.createElement("div");
        nm.className = "pinfo-name";
        nm.textContent = plain(this.displayName);
        col.appendChild(nm);

        // NameMC button (skipped for Bedrock players whose name starts with "-")
        if (window.agoniaNameMc) col.insertAdjacentHTML("beforeend", window.agoniaNameMc(this.uuid, this.name));

        // membership badge (ägare / prime / veteran)
        if (window.agoniaMembership) col.insertAdjacentHTML("beforeend", window.agoniaMembership(status));

        c.appendChild(col);
        return c;
    }

    update(player) {
        this.x = player.x;
        this.z = player.z;
        this.world = player.world;
        this.armor = player.armor;
        this.health = player.health;
        this.displayName = player.display_name !== undefined ? player.display_name : player.name;
        this.lastJson = player;

        const link = document.getElementById(player.uuid);
        const img = link ? link.getElementsByTagName("img")[0] : null;
        const span = link ? link.getElementsByTagName("span")[0] : null;

        if (S.worldList.curWorld.name === player.world) {
            if (S.worldList.curWorld.player_tracker.enabled) {
                this.addMarker();
            }
            const latlng = S.toLatLng(player.x, player.z);
            if (!this.marker.getLatLng().equals(latlng)) {
                // Move the head, its cone, and its nameplate in ONE step so all
                // three start the same CSS glide on the same frame — this is what
                // keeps the name locked to the head instead of lagging behind.
                this.marker.setLatLng(latlng);
                this.beam.setLatLng(latlng);
                if (this.nameAdded) {
                    this.nameMarker.setLatLng(latlng);
                }
            }
            this.aimBeam(player.yaw);
            this.syncBeam(); // yaw may have only just appeared in the feed
            this.syncName();
            this.applyActivity();
            if (img) img.classList.remove("other-world");
            if (span) span.classList.remove("other-world");
        } else {
            this.removeMarker();
            if (img) img.classList.add("other-world");
            if (span) span.classList.add("other-world");
        }

        // only when it actually changed: this runs once a second per player
        if (this.shownName !== this.displayName) {
            if (this.nameAlways) {
                this.paintName();
            } else if (this.tooltip) {
                this.tooltip.setContent(this.makeNameContent());
            }
        }
        if (this.marker.isPopupOpen && this.marker.isPopupOpen()) {
            // keep the open popup attached to the head as it moves (glides via CSS)
            const popup = this.marker.getPopup && this.marker.getPopup();
            if (popup) popup.setLatLng(S.toLatLng(player.x, player.z));
            this.marker.setPopupContent(this.makeInfoContent(player));
        }
    }

    removeMarker() {
        this.marker.remove();
        S.playerList.markers.delete(this.uuid);
        S.map.removeLayer(this.marker);
        S.layerControl.playersLayer.removeLayer(this.marker);
        this.syncBeam(); // the head is gone -> so is its cone of light
        this.syncName(); // ...and its nameplate
    }

    addMarker() {
        if (!S.playerList.markers.has(this.uuid)) {
            this.marker.addTo(S.layerControl.playersLayer);
            S.playerList.markers.set(this.uuid, this.marker);
        }
        this.syncBeam();
        this.syncName();
        this.applyActivity();
    }
}

export { Player };
